import React, { useState } from 'react';
import { useFormikContext } from 'formik';
import { ExternalLink, Eye } from 'lucide-react';
import {
  mergeTheme,
  resolveLinkInBioItems,
  toLinkInBioProfileView,
  type LinkInBio,
  type LinkInBioPageSummary,
} from '@repo/lib';
import { LinkInBioView, formatLinkInBioEventDate, getEnglishLinkInBioLabels } from '@repo/checkout';
import shouldShowLinkInBioBranding from '@repo/checkout/lib/linkInBio/shouldShowLinkInBioBranding';
import resolveAffiliateBadgeToken from '@repo/checkout/lib/resolveAffiliateBadgeToken';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import PreviewFontLoader from '@dashboard/components/PreviewFontLoader';
import { useSellerThemes } from '@dashboard/hooks/queries/useThemeQueries';
import { Button } from '@dashboard/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@dashboard/components/ui/sheet';
import {
  getLinkInBioHomepageUrl,
  getLinkInBioPreviewPaths,
  getLinkInBioShortUrl,
  LINK_IN_BIO_SELLER_VISIT,
  withLinkInBioQuery,
} from '@dashboard/services/getLinkInBioUrl';

const labels = getEnglishLinkInBioLabels();

type PreviewProps = {
  pages: Record<string, LinkInBioPageSummary>;
  upcomingEvents: LinkInBioPageSummary[];
};

function PreviewContent({ pages, upcomingEvents }: PreviewProps) {
  const { seller } = useAuth();
  const { values } = useFormikContext<LinkInBio>();
  const { data: themes } = useSellerThemes();
  const locale = typeof navigator === 'undefined' ? 'en-US' : navigator.language;
  const byId = (id?: string | null) =>
    themes?.find((candidate: { _id: string }) => candidate._id === id);
  const theme = byId(values.theme) ?? byId(seller.defaultTheme) ?? null;

  const items = resolveLinkInBioItems({
    blocks: values.blocks ?? [],
    pages,
    upcomingEvents,
    now: new Date(),
    paths: getLinkInBioPreviewPaths(seller),
    formatEventDate: event => formatLinkInBioEventDate(event, locale),
  });

  return (
    <>
      <PreviewFontLoader
        theme={mergeTheme(theme, null, seller, { brandColor: 'fillsUnsetColors' })}
      />
      <LinkInBioView
        className="min-h-full"
        profile={toLinkInBioProfileView(values, seller)}
        items={items}
        labels={labels}
        theme={theme}
        linkTarget="_blank"
        showBranding={shouldShowLinkInBioBranding(seller, values)}
        affiliateToken={resolveAffiliateBadgeToken(seller)}
      />
    </>
  );
}

function PhoneFrame({ height, children }: { height: string; children?: React.ReactNode }) {
  return (
    <div
      className="relative mx-auto aspect-[9/19.5] max-w-full rounded-[2.75rem] bg-gray-950 p-2.5 shadow-2xl ring-1 ring-black/10 dark:ring-white/20"
      style={{ height }}
    >
      <div
        className="pointer-events-none absolute left-1/2 top-4 z-20 h-6 w-24 -translate-x-1/2 rounded-full bg-gray-950"
        aria-hidden="true"
      />
      {/* The frame's colour, not white: this background shows through the rounded clip's edge. */}
      <div
        className="h-full overflow-y-auto overscroll-contain rounded-[2.1rem] bg-gray-950 [scrollbar-width:none]"
        role="region"
        aria-label="Preview of your link in bio"
      >
        <div
          className="pointer-events-none sticky top-0 z-10 -mb-14 h-14 backdrop-blur-md [mask-image:radial-gradient(ellipse_50%_100%_at_top,black_50%,transparent)]"
          aria-hidden="true"
        />
        {children}
      </div>
    </div>
  );
}

function useShownUrl() {
  const { seller } = useAuth();
  return seller.homepage?.type === 'linkInBio'
    ? getLinkInBioHomepageUrl(seller)
    : (getLinkInBioShortUrl(seller) ?? '');
}

const PANEL_HEIGHT = 'min(760px, calc(100vh - 9.5rem))';

export default function LinkInBioPreview({ isLive, ...props }: PreviewProps & { isLive: boolean }) {
  const url = useShownUrl();

  return (
    <div className="flex flex-col gap-3 rounded-xl border border-gray-100 bg-neutralgray p-3 dark:border-border dark:bg-background">
      <div className="flex items-center gap-2">
        <p className="text-sm font-medium">Preview</p>
        {isLive && url && (
          <Button variant="ghost" size="icon-sm" className="ml-auto" asChild>
            <a
              href={withLinkInBioQuery(url, LINK_IN_BIO_SELLER_VISIT)}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Open live page"
            >
              <ExternalLink />
            </a>
          </Button>
        )}
      </div>
      <PhoneFrame height={PANEL_HEIGHT}>
        <PreviewContent {...props} />
      </PhoneFrame>
    </div>
  );
}

export function LinkInBioPreviewSheet(props: PreviewProps) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Button
        type="button"
        className="fixed bottom-4 right-4 z-40 rounded-full shadow-lg lg:hidden"
        onClick={() => setOpen(true)}
      >
        <Eye />
        Preview
      </Button>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent
          side="bottom"
          className="flex h-[100dvh] flex-col gap-0 bg-neutralgray p-0 dark:bg-background"
        >
          <SheetHeader className="px-4 pb-2 pt-4">
            <SheetTitle>Preview</SheetTitle>
            <SheetDescription>How visitors see your link in bio on a phone.</SheetDescription>
          </SheetHeader>
          <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-6">
            <PhoneFrame height="min(760px, calc(100dvh - 8rem))">
              <PreviewContent {...props} />
            </PhoneFrame>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
